/** Cell / CellGroup — DS `Cell` (Spec 020). The atom of every list in the app. */

import { Children, isValidElement, type ElementType, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/cn";

export interface CellProps {
  title: ReactNode;
  /** Secondary line under the title, 13/18 `text-muted`. */
  subtitle?: ReactNode;
  /** Leading icon, rendered at 20px per §6. */
  icon?: ReactNode;
  /** Right-hand value, single line, ellipsised. */
  value?: ReactNode;
  /** Arbitrary trailing content; takes precedence over `value`. */
  trailing?: ReactNode;
  /** §7 — chevron means "this navigates". Implied by `to`. */
  chevron?: boolean;
  onClick?: () => void;
  /** Renders the whole row as a router link. */
  to?: string;
  disabled?: boolean;
  /** §7 — the done treatment: strike the title and dim the row. */
  done?: boolean;
  /** «Выйти», «Удалить»: the icon joins the text colour. Never red (Spec 020 D2). */
  destructive?: boolean;
  className?: string;
}

/**
 * DS: min-height 48, padding 10/16. The entire row is the tap target (§5),
 * never just the icon or the chevron.
 */
export function Cell({
  title,
  subtitle,
  icon,
  value,
  trailing,
  chevron,
  onClick,
  to,
  disabled,
  done,
  destructive,
  className,
}: CellProps) {
  const interactive = !disabled && (typeof onClick === "function" || typeof to === "string");
  const showChevron = chevron ?? typeof to === "string";

  const body = (
    <>
      {icon != null && (
        <span
          className={cn(
            "flex h-5 w-5 shrink-0 items-center justify-center",
            destructive ? "text-p-text" : "text-p-text-muted",
          )}
        >
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block text-cell-title text-p-text",
            done && "done-strike text-p-text-muted",
          )}
        >
          {title}
        </span>
        {subtitle != null && (
          <span className="mt-0.5 block text-meta text-p-text-muted">{subtitle}</span>
        )}
      </span>
      {trailing ??
        (value != null && (
          <span className="truncate-line max-w-[45%] shrink-0 text-body text-p-text-muted">{value}</span>
        ))}
      {showChevron && (
        <ChevronRight className="h-[18px] w-[18px] shrink-0 text-p-text-muted" aria-hidden="true" />
      )}
    </>
  );

  const classes = cn(
    "flex w-full min-h-12 items-center gap-3 px-4 py-2.5 text-left",
    interactive && "press-surface",
    // §5 — disabled cells sit at .4, buttons at .35.
    disabled && "opacity-40",
    done && "done-dim",
    className,
  );

  if (to && !disabled) {
    return (
      <Link to={to} className={classes}>
        {body}
      </Link>
    );
  }

  const Tag: ElementType = interactive ? "button" : "div";
  return (
    <Tag
      type={interactive ? "button" : undefined}
      onClick={interactive ? onClick : undefined}
      disabled={Tag === "button" ? disabled : undefined}
      className={classes}
    >
      {body}
    </Tag>
  );
}

/**
 * A group is one `surface` with a hairline border and radius 12; only the
 * outer corners round, and cells are parted by a hairline.
 *
 * That hairline is inset 16 from the left, or 48 when the cells carry icons
 * (16 + 20 + 12), so the rule starts under the text rather than the icon.
 */
export function CellGroup({
  children,
  inset = "text",
  className,
}: {
  children: ReactNode;
  inset?: "text" | "icon";
  className?: string;
}) {
  const items = Children.toArray(children).filter(isValidElement);
  return (
    <div className={cn("overflow-hidden rounded-card border border-p-border bg-p-surface", className)}>
      {items.map((child, i) => (
        <div key={child.key ?? i}>
          {i > 0 && (
            <div
              aria-hidden="true"
              className="h-px bg-p-border"
              style={{ marginLeft: inset === "icon" ? 48 : 16 }}
            />
          )}
          {child}
        </div>
      ))}
    </div>
  );
}

/** A standalone cell keeps its own radius (§7). */
export function SoloCell(props: CellProps) {
  return (
    <div className="overflow-hidden rounded-card border border-p-border bg-p-surface">
      <Cell {...props} />
    </div>
  );
}
